import { Suspense, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import T, { useTranslation } from '@wojtekmaj/react-t';

import Button from '@rewardopl/react-ui/button';
import Dialog from '@rewardopl/react-ui/dialog';
import RadioField from '@rewardopl/react-ui/radio_field';

import { ButtonWrapper, Form, List, Wrapper } from './dialog.styles';
import { Name } from './option.styles';

import LoadingText from '../../../loading_text';
import Option from './option';

import {
  currentIntegrationIdState,
  integrationsState,
  preferredIntegrationIdState,
} from '../../../../store';

import type { Integration } from '@rewardopl/types';

type ContentProps = {
  onDismiss?: () => void;
};

function Content({ onDismiss }: ContentProps) {
  const integrations = useAtomValue(integrationsState);
  const currentIntegrationId = useAtomValue(currentIntegrationIdState);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    Integration['_id'] | null | undefined
  >(currentIntegrationId);
  const setPreferredIntegrationId = useSetAtom(preferredIntegrationIdState);

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setPreferredIntegrationId(selectedIntegrationId);

    if (onDismiss) {
      onDismiss();
    }
  }

  return (
    <Wrapper>
      <Form onSubmit={onSubmit}>
        <p>
          <T>Please select an option to continue.</T>
        </p>
        <List>
          {integrations.map((integration) => (
            <Option
              checked={selectedIntegrationId === integration._id}
              integrationId={integration._id}
              integrationType={integration.type}
              key={integration._id}
              onChange={setSelectedIntegrationId}
            />
          ))}
          <RadioField
            checked={selectedIntegrationId === null}
            label={
              <Name>
                <T>None</T>
              </Name>
            }
            name="integration"
            onChange={() => setSelectedIntegrationId(null)}
            value="null"
          />
        </List>
        <ButtonWrapper>
          <Button className="primary" disabled={selectedIntegrationId === undefined} type="submit">
            <T>Save</T>
          </Button>
        </ButtonWrapper>
      </Form>
    </Wrapper>
  );
}

type SelectIntegrationDialogProps = {
  mustDisplayDialog: boolean;
  onDismiss?: () => void;
};

export default function SelectIntegrationDialog({
  mustDisplayDialog,
  onDismiss,
}: SelectIntegrationDialogProps) {
  const titleString = useTranslation('Select integration');

  return (
    <Dialog
      flexContent
      isOpen={location.hash === '#select-integration' || mustDisplayDialog}
      onDismiss={onDismiss}
      title={titleString}
    >
      <Suspense fallback={<LoadingText />}>
        <Content onDismiss={onDismiss} />
      </Suspense>
    </Dialog>
  );
}
